import React, { useState } from 'react';
import { Menu, X, LogOut, User, Home, Briefcase, HelpCircle } from 'lucide-react';
import { signOut } from 'firebase/auth';
import { auth } from '../../config/firebase';
import { useNavigate } from 'react-router-dom';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  accent: '#C9A961',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
};

const Header = ({ user, userProfile, currentView, onViewChange }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const menuItems = [
    {
      id: 'home',
      label: 'Dashboard',
      icon: Home,
      roles: ['professional', 'employer'],
    },
    {
      id: 'profile',
      label: 'View Profile',
      icon: User,
      roles: ['professional', 'employer'],
    },
    {
      id: 'bookings',
      label: 'Bookings',
      icon: Briefcase,
      roles: ['professional', 'employer'],
    },
    {
      id: 'support',
      label: 'Contact Us',
      icon: HelpCircle,
      roles: ['professional', 'employer'],
      path: '/support' // Helper for navigation logic
    },
  ];

  const filteredMenuItems = menuItems.filter(item =>
    item.roles.includes(userProfile?.role)
  );

  return (
    <>
      {/* Header */}
      <header className="bg-white" style={{ backgroundColor: BRAND_COLORS.primary, borderBottom: `2px solid ${BRAND_COLORS.accent}` }}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 hover:bg-gray-800 transition-colors rounded"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6" style={{ color: BRAND_COLORS.accent }} />
                ) : (
                  <Menu className="w-6 h-6" style={{ color: BRAND_COLORS.accent }} />
                )}
              </button>
              <h1
                className="text-2xl font-light tracking-widest cursor-pointer"
                style={{ color: BRAND_COLORS.accent }}
                onClick={() => onViewChange('home')}
              >
                SKILLVEND
              </h1>
            </div>

            {/* User Info & Sign Out */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-light hidden sm:block" style={{ color: BRAND_COLORS.light }}>
                {userProfile?.displayName || user?.email}
              </span>
              <button
                onClick={handleSignOut}
                className="flex items-center space-x-2 px-4 py-2 border-2 hover:bg-gray-800 transition-colors"
                style={{ borderColor: BRAND_COLORS.accent }}
              >
                <LogOut className="w-4 h-4" style={{ color: BRAND_COLORS.accent }} />
                <span className="text-sm font-light hidden sm:inline" style={{ color: BRAND_COLORS.accent }}>
                  Sign Out
                </span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Flyout Menu */}
      <div
        className={`fixed left-0 bg-white border-r-2 transform transition-transform duration-300 ease-in-out z-40 ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        style={{
          top: '80px',
          width: '280px',
          height: 'calc(100vh - 80px)',
          borderColor: BRAND_COLORS.primary,
        }}
      >
        <nav className="p-6">
          <div className="space-y-2">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    if (item.path) {
                      const role = userProfile?.role;
                      if (role) {
                        navigate(`/${role}${item.path}`);
                      }
                    } else {
                      onViewChange(item.id);
                    }
                    setIsMenuOpen(false);
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-3 transition-colors ${currentView === item.id
                    ? 'border-2'
                    : 'hover:bg-gray-50'
                    }`}
                  style={{
                    borderColor: currentView === item.id ? BRAND_COLORS.accent : 'transparent',
                  }}
                >
                  <Icon
                    className="w-5 h-5"
                    style={{
                      color: currentView === item.id ? BRAND_COLORS.accent : BRAND_COLORS.primary
                    }}
                  />
                  <span
                    className="font-light"
                    style={{
                      color: currentView === item.id ? BRAND_COLORS.accent : BRAND_COLORS.primary
                    }}
                  >
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>
      </div>

      {/* Overlay */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30"
          style={{ top: '80px' }}
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </>
  );
};

export default Header;
